import { BookingNotifications, FilterAndSortNotifications } from "components/BookingNotifications/BookingNotifications";
import { FilterNotificationState } from "components/BookingNotifications/Types";
import { useBookingClient } from "hooks/useHttpClient";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { Session_Reducer_PushTransaction } from "rootExports/SessionReducer";
import { BookingItemModel, ConfirmNotifications, ENotificationActionRequiredEnumModel, ENotificationCategoryEnumModel, UpdateNotificationModel } from "WebApiClient";
import { toast } from "react-toastify";
import { State } from "rootExports/rootReducer";

interface BookingManagerManageNotificationModalProps {
  Booking: BookingItemModel;
  Toggle: () => void;
  IsOpen: boolean;
  OnUpdate: (data: BookingItemModel) => void;
}

export const BookingManagerManageNotificationModal: React.FC<
  BookingManagerManageNotificationModalProps
> = (props) => {
  const bookClient = useBookingClient();
  const [errorOccured, setErrorOccured] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [newNotification, setNewNotification] = useState<UpdateNotificationModel | undefined>(undefined);
  const dispatch = useDispatch();
  const [filterState, setFilterState] = useState<FilterNotificationState>({
    ActionRequired: [ENotificationActionRequiredEnumModel.Urgent, ENotificationActionRequiredEnumModel.None, ENotificationActionRequiredEnumModel.Medium, ENotificationActionRequiredEnumModel.Low, ENotificationActionRequiredEnumModel.High],
    Descending: true, SortBy: "Priority",
    Category: [ENotificationCategoryEnumModel.BookingFailed, ENotificationCategoryEnumModel.OCN, ENotificationCategoryEnumModel.Other, ENotificationCategoryEnumModel.PaymentRejected, ENotificationCategoryEnumModel.QueueRobot, ENotificationCategoryEnumModel.Remark, ENotificationCategoryEnumModel.Reprice, ENotificationCategoryEnumModel.TTL]
  });

  //extracting admin fields container data 
  const otherSettings: string = useSelector((state: State) => {
    if (
      state.Session.FrontendSettings.otherSettingsAsKeyValueText ==
      null ||
      state.Session.FrontendSettings.otherSettingsAsKeyValueText.trim() ===
      ""
    ) {
      return state.Session.FrontendSettings
        .otherSettingsAsKeyValueText ===
        "menuPosition=L\nshowFareHeader=Off"
        ? "menuPosition=L\nshowFareHeader=Off\nshowSmartCalender=None/n[]/nfalse/nfalse"
        : `menuPosition=L/{"textColor":"#ffffff","bgColor":"#242424"}
            showFareHeader=Off
            showSmartCalender=None/n[]/nfalse/nfalse{"Economy":{"Checked":true,"preSelect":true},"Premium":{"Checked":true,"preSelect":false},"Business":{"Checked":true,"preSelect":false},"First":{"Checked":true,"preSelect":false}}\n0\nshowHotelTab=None\nshowCabinContainer=All`;
    }
    return state.Session.FrontendSettings.otherSettingsAsKeyValueText;
  });

  const splitSetting = otherSettings.split("\n");

  let containerString = "";

  if (splitSetting.length > 7 && splitSetting[7]?.trim()) {
    try {
      const parsed = JSON.parse(splitSetting[7]);
      // Check if it's a valid object and has Container key
      if (typeof parsed === "object" && parsed !== null && "Container" in parsed) {
        containerString = parsed.Container ?? "";
      }
    } catch (e) {
      console.warn("Invalid JSON in splitSetting[7]:", splitSetting[7]);
      // fallback to blank
      containerString = "";
    }
  } else {
    // Index 7 doesn't exist, return blank
    containerString = "";
  }

  function hasMatchingCarrierCode(containerStr: string, code: string): boolean {
    const match = containerStr?.match(/CONFIRMOCN:([^#]+)/);
    if (!match) return false;

    const inputCodes = match[1].split(",").map(c => c.trim());
    return inputCodes.includes(code);
  }

  // number of notification 
  const filteredNotifications = FilterAndSortNotifications(props.Booking.latestVersion.notifications, filterState);
  const hasCategoryOne = filteredNotifications.some(item => item.category === 1);

  async function OnDeleteNotification() {
    try {
      setFetching(true);
      const { notifications, bookingIdentifier } = props.Booking.latestVersion;
      for (const notification of notifications) {
        try {
          const response = await bookClient.deleteNotification({
            identifier: bookingIdentifier,
            notificationID: notification.notificationID,
          });

          // Dispatch transaction to the store
          dispatch(Session_Reducer_PushTransaction(response.responseMetaData));

          // Update the status message and error status based on the response
          if (response.responseMetaData.errorOccured) {
            // setStatusMessage("Notification could not be deleted!");
            setErrorOccured(true);
          } else {
            // setStatusMessage("Notification deleted.");
            setErrorOccured(false);
          }
        } catch (error) {
          // Handle individual notification deletion errors
          setErrorOccured(true);
          console.error(`Failed to delete notification with ID: ${notification.notificationID}`, error);
        }
      }
    } catch (globalError) {
      console.error("Error occurred during notification deletion process:", globalError);
      setErrorOccured(true);
      OnFetch()
    } finally {
      setFetching(false);
      OnFetch()
    }
  }

  function OnFetch() {
    setFetching(true);
    setNewNotification(undefined);
    bookClient.getBookingDetails(props.Booking.latestVersion.bookingIdentifier)
      .then(response => {
        if (response.result) {
          props.OnUpdate(response.result)
        }
        dispatch(Session_Reducer_PushTransaction(response.responseMetaData));
        if (!response.result) {
          setErrorOccured(true)
        }
        else {
          setErrorOccured(false);
        }

      })
      .catch(() => {
        setErrorOccured(true);
      })
      .finally(() => {
        setFetching(false);
      })
  }

  function onConfirm() {
    const confirmReqModel: ConfirmNotifications = {
      BookingIdentifier: {
        CartId: props.Booking.latestVersion.bookingIdentifier?.cartId,
        BookingId: props.Booking.latestVersion.bookingIdentifier?.bookingId
      }
    }
    setFetching(true);
    bookClient.confirmChanges(confirmReqModel).then(res => {
      if (res.responseMetaData.errorOccured) {
        toast.error(res?.responseMetaData?.errorReason?.errorMessage, { autoClose: 3000 });
      }
      if (!res.responseMetaData.errorOccured) {
        toast.success("Confirmation successfull", { autoClose: 1500 });
      }
    }).catch((err) => {
      setErrorOccured(true);
    }).finally(() => { setFetching(false); })
  }

  return (
    <Modal
      size="xl"
      centered
      toggle={props.Toggle}
      isOpen={props.IsOpen}
      scrollable={true}>
      <ModalHeader toggle={props.Toggle}>
        Manage notifications for{" "}
        <strong> {props.Booking.latestVersion.generalInfo.filekey}</strong>
      </ModalHeader>

      <ModalBody>
        <BookingNotifications
          Booking={props.Booking}
          OnUpdate={props.OnUpdate}
          OnFetch={OnFetch}
          fetching={fetching}
          errorOccured={errorOccured}
          newNotification={newNotification}
          setFilterState={setFilterState}
          filterState={filterState}
        />
      </ModalBody>

      <ModalFooter className={filteredNotifications?.length != 0 ? "justify-space-between NotificationModalMR" : "justify-space-end "}>
        {
          filteredNotifications?.length != 0 &&
          <Button color="primary" onClick={OnDeleteNotification}>
            Delete all notifications
          </Button>
        }
        {(filterState?.Category.includes(1) && filteredNotifications?.length != 0) && hasCategoryOne &&
          hasMatchingCarrierCode(containerString, props.Booking.latestVersion.generalInfo.platingCarrier.code) &&
          <Button color="primary" onClick={onConfirm} className="cnfOCNBtn">
            Confirm OCN
          </Button>
        }
        <Button color="primary" onClick={props.Toggle}>
          Close
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default BookingManagerManageNotificationModal;
